import { useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import {
  authApi,
  type TwoFactorSetupResponse,
  type TwoFactorStatus,
} from '@/api/auth';
import { errorMessage } from '@/api/client';
import {
  base64ToBytes,
  bytesToBase64,
  computeAuthHash,
  deriveMasterKey,
  ensureSodium,
  kdfParamsFromServer,
} from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import libsodium from 'libsodium-wrappers-sumo';
import { copySecret } from '@/lib/clipboard';
import { formatDateTime } from '@/lib/format';
import i18n from '@/i18n';

type WizardStep = 'idle' | 'scan' | 'confirm' | 'codes';
type DisableMode = 'totp' | 'recovery';

export default function TwoFactorSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['2fa-status'],
    queryFn: () => authApi.twoFactorStatus(),
  });

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{t('settings.two_factor')}</div>
          <div className="text-xs text-[var(--text-dim)]">
            {t('settings.two_factor_description')}
          </div>
        </div>
        <StatusBadge status={status.data} />
      </div>

      {status.isLoading && (
        <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
      )}

      {status.data && !status.data.enabled && <SetupWizard onDone={refetch} />}

      {status.data && status.data.enabled && (
        <EnabledPanel status={status.data} onChanged={refetch} />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status?: TwoFactorStatus }) {
  const { t } = useTranslation();
  if (!status) return null;
  if (status.enabled) {
    return (
      <span
        className="badge text-[10px]"
        style={{
          background: 'var(--accent-bg)',
          color: 'var(--accent)',
          border: '1px solid var(--accent)',
        }}
      >
        {t('security_section.two_factor_enabled')}
      </span>
    );
  }
  return (
    <span
      className="badge text-[10px]"
      style={{ background: 'var(--bg-surface)', color: 'var(--text-muted)' }}
    >
      {t('security_section.two_factor_not_enabled')}
    </span>
  );
}

// ============================================================
// Setup wizard
// ============================================================

function SetupWizard({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<WizardStep>('idle');
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);

  const beginMutation = useMutation({
    mutationFn: () => authApi.twoFactorSetup(),
    onSuccess: async (data) => {
      setSetupData(data);
      try {
        const url = await QRCode.toDataURL(data.otpauth_url, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 240,
          color: { dark: '#000000', light: '#ffffff' },
        });
        setQrDataUrl(url);
      } catch {
        setQrDataUrl(null);
      }
      setStep('scan');
    },
    onError: (err) => setError(errorMessage(err, t('security_section.setup_start_error'))),
  });

  const confirmMutation = useMutation({
    mutationFn: () =>
      authApi.twoFactorConfirm({
        setup_token: setupData!.setup_token,
        totp_code: code.trim(),
      }),
    onSuccess: (data) => {
      setRecoveryCodes(data.recovery_codes);
      setStep('codes');
    },
    onError: (err) => setError(errorMessage(err, t('security_section.invalid_or_expired'))),
  });

  const reset = () => {
    setStep('idle');
    setSetupData(null);
    setQrDataUrl(null);
    setCode('');
    setRecoveryCodes(null);
    setSaved(false);
    setError(null);
  };

  if (step === 'idle') {
    return (
      <button
        type="button"
        className="btn-outline text-xs"
        onClick={() => {
          setError(null);
          beginMutation.mutate();
        }}
        disabled={beginMutation.isPending}
      >
        {beginMutation.isPending ? t('security_section.starting') : t('security_section.configure')}
      </button>
    );
  }

  if (step === 'scan' && setupData) {
    return (
      <div className="border border-[var(--border)] rounded-md p-4 space-y-3">
        <div className="text-sm font-medium">{t('security_section.setup_step1')}</div>
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={t('security_section.qr_alt')}
            width={240}
            height={240}
            style={{ imageRendering: 'pixelated' }}
          />
        ) : (
          <p className="text-xs text-[var(--text-muted)]">{t('security_section.qr_render_fail')}</p>
        )}
        <details className="text-xs">
          <summary className="cursor-pointer text-[var(--text-muted)]">
            {t('security_section.manual_entry')}
          </summary>
          <div className="mt-2 space-y-1 font-mono text-[11px]">
            <div><span className="text-[var(--text-muted)]">{t('security_section.issuer')}:</span> {setupData.issuer}</div>
            <div><span className="text-[var(--text-muted)]">{t('security_section.account')}:</span> {setupData.account}</div>
            <div>
              <span className="text-[var(--text-muted)]">{t('security_section.secret')}:</span>{' '}
              <span className="select-all">{setupData.secret_b32}</span>
            </div>
          </div>
        </details>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => {
              setError(null);
              setStep('confirm');
            }}
          >
            {t('security_section.scanned_continue')}
          </button>
          <button type="button" className="btn-outline text-xs" onClick={reset}>
            {t('security_section.discard')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'confirm' && setupData) {
    return (
      <div className="border border-[var(--border)] rounded-md p-4 space-y-3">
        <div className="text-sm font-medium">{t('security_section.setup_step2')}</div>
        <p className="text-xs text-[var(--text-muted)]">
          {t('security_section.verify_code_hint')}
        </p>
        <input
          className="input font-mono text-lg tracking-widest text-center"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          autoFocus
          disabled={confirmMutation.isPending}
        />
        {error && <ErrorBanner text={error} />}
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={code.length !== 6 || confirmMutation.isPending}
            onClick={() => {
              setError(null);
              confirmMutation.mutate();
            }}
          >
            {confirmMutation.isPending ? t('security_section.verify') : t('security_section.activate_2fa')}
          </button>
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={() => setStep('scan')}
            disabled={confirmMutation.isPending}
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'codes' && recoveryCodes) {
    return (
      <div className="border border-[var(--border)] rounded-md p-4 space-y-3">
        <div className="text-sm font-medium">
          {t('security_section.setup_step3')}
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          <Trans i18nKey="security_section.codes_help" components={{ strong: <strong /> }} />
          {' '}
          {t('security_section.codes_save_where')}
        </p>
        <RecoveryCodesBox codes={recoveryCodes} />
        <label className="flex items-center gap-2 text-xs pt-1">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
          />
          {t('security_section.codes_saved')}
        </label>
        <button
          type="button"
          className="btn-primary text-xs"
          disabled={!saved}
          onClick={() => {
            reset();
            onDone();
          }}
        >
          {t('common.done')}
        </button>
      </div>
    );
  }

  return null;
}

// ============================================================
// Enabled panel — disable + regenerate recovery
// ============================================================

function EnabledPanel({
  status,
  onChanged,
}: {
  status: TwoFactorStatus;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'idle' | 'disable' | 'regenerate'>('idle');
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);

  return (
    <div className="border border-[var(--border)] rounded-md p-4 space-y-3">
      <div className="text-xs text-[var(--text-muted)]">
        <Trans
          i18nKey="security_section.enabled_at_with_codes"
          values={{
            when: formatDateTime(status.enabled_at),
            count: status.recovery_codes_remaining,
          }}
          components={{ strong: <strong /> }}
        />
      </div>
      {status.recovery_codes_remaining <= 2 && status.recovery_codes_remaining > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {t('security_section.low_recovery_warn')}
        </p>
      )}
      {status.recovery_codes_remaining === 0 && (
        <p className="text-xs text-[var(--danger)]">
          {t('security_section.no_recovery_warn')}
        </p>
      )}

      {mode === 'idle' && (
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={() => setMode('regenerate')}
          >
            {t('security_section.regenerate_codes')}
          </button>
          <button
            type="button"
            className="text-xs text-[var(--danger)] hover:underline"
            onClick={() => setMode('disable')}
          >
            {t('security_section.disable')}
          </button>
        </div>
      )}

      {mode === 'disable' && (
        <DisableForm
          onCancel={() => setMode('idle')}
          onDone={() => {
            setMode('idle');
            onChanged();
          }}
        />
      )}

      {mode === 'regenerate' && !regenCodes && (
        <RegenerateForm
          onCancel={() => setMode('idle')}
          onDone={(codes) => {
            setRegenCodes(codes);
            onChanged();
          }}
        />
      )}

      {mode === 'regenerate' && regenCodes && (
        <div className="space-y-3">
          <p className="text-xs text-[var(--text-muted)]">
            {t('security_section.new_codes_invalidated')}
          </p>
          <RecoveryCodesBox codes={regenCodes} />
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => {
              setRegenCodes(null);
              setMode('idle');
            }}
          >
            {t('security_section.saved_button')}
          </button>
        </div>
      )}
    </div>
  );
}

function DisableForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [method, setMethod] = useState<DisableMode>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const authHash = await deriveAuthHash(password);
      const payload =
        method === 'totp'
          ? { auth_hash: authHash, totp_code: code.trim() }
          : { auth_hash: authHash, recovery_code: code.trim() };
      await authApi.twoFactorDisable(payload);
      onDone();
    } catch (err) {
      setError(errorMessage(err, t('security_section.disable_failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-[var(--border)] pt-3">
      <p className="text-xs text-[var(--text-muted)]">
        {t('security_section.confirm_with_master_pwd_totp_recovery')}
      </p>
      <input
        type="password"
        className="input font-mono text-xs"
        placeholder={t('auth.master_password')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={busy}
      />
      <div className="flex gap-2">
        <button
          type="button"
          className={`btn-outline text-xs flex-1 ${method === 'totp' ? 'ring-1 ring-[var(--accent)]' : ''}`}
          onClick={() => {
            setMethod('totp');
            setCode('');
          }}
          disabled={busy}
        >
          {t('security_section.totp_code_label')}
        </button>
        <button
          type="button"
          className={`btn-outline text-xs flex-1 ${method === 'recovery' ? 'ring-1 ring-[var(--accent)]' : ''}`}
          onClick={() => {
            setMethod('recovery');
            setCode('');
          }}
          disabled={busy}
        >
          {t('security_section.recovery_code_label')}
        </button>
      </div>
      <input
        className="input font-mono text-xs"
        placeholder={method === 'totp' ? '000000' : 'XXXX-XXXX'}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        maxLength={method === 'totp' ? 6 : 12}
        disabled={busy}
      />
      {error && <ErrorBanner text={error} />}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary text-xs"
          style={{ background: 'var(--danger)' }}
          onClick={submit}
          disabled={busy || !password || !code}
        >
          {busy ? t('security_section.processing') : t('security_section.disable')}
        </button>
        <button type="button" className="btn-outline text-xs" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

function RegenerateForm({
  onCancel,
  onDone,
}: {
  onCancel: () => void;
  onDone: (codes: string[]) => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const authHash = await deriveAuthHash(password);
      const resp = await authApi.twoFactorRegenerateRecovery({
        auth_hash: authHash,
        totp_code: code.trim(),
      });
      onDone(resp.recovery_codes);
    } catch (err) {
      setError(errorMessage(err, t('security_section.regen_failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-[var(--border)] pt-3">
      <p className="text-xs text-[var(--text-muted)]">
        {t('security_section.confirm_with_master_pwd_totp')}
      </p>
      <input
        type="password"
        className="input font-mono text-xs"
        placeholder={t('auth.master_password')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={busy}
      />
      <input
        className="input font-mono text-xs"
        placeholder="000000"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        maxLength={6}
        disabled={busy}
      />
      {error && <ErrorBanner text={error} />}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={submit}
          disabled={busy || !password || code.length !== 6}
        >
          {busy ? t('security_section.processing') : t('security_section.regenerate')}
        </button>
        <button type="button" className="btn-outline text-xs" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

function RecoveryCodesBox({ codes }: { codes: string[] }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const joined = codes.join('\n');

  const copy = async () => {
    await copySecret(joined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([joined], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cerbes-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-2">
      <div
        className="grid grid-cols-2 gap-1 p-3 font-mono text-sm rounded"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        {codes.map((c) => (
          <div key={c} className="select-all">
            {c}
          </div>
        ))}
      </div>
      <textarea
        ref={textRef}
        value={joined}
        readOnly
        className="hidden"
        aria-hidden="true"
      />
      <div className="flex gap-2">
        <button type="button" className="btn-outline text-xs" onClick={copy}>
          {copied ? t('security_section.copied') : t('security_section.copy_all')}
        </button>
        <button type="button" className="btn-outline text-xs" onClick={download}>
          {t('security_section.download_txt')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

async function deriveAuthHash(password: string): Promise<string> {
  await ensureSodium();
  const { user } = useAuthStore.getState();
  if (!user) throw new Error(i18n.t('security_section.session_expired'));
  const salt = await authApi.getOwnKdf();
  const masterKey = await deriveMasterKey(
    password,
    base64ToBytes(salt.kdf_salt),
    kdfParamsFromServer(salt.kdf_params),
  );
  const authHash = await computeAuthHash(masterKey);
  libsodium.memzero(masterKey);
  const result = bytesToBase64(authHash);
  libsodium.memzero(authHash);
  return result;
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <div
      className="text-xs rounded px-3 py-2"
      style={{
        color: 'var(--danger)',
        background: 'var(--danger-bg)',
        border: '1px solid var(--danger)',
      }}
    >
      {text}
    </div>
  );
}
